// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Deciding that a button is pressed, and staying decided (M5-05, M5-07, XR-004).
//
// One gate per pressable control. It is the whole of XR-004's second mitigation — "generous
// volume with a firm threshold" — plus the hysteresis that makes it usable by a hand that is not
// resting on anything.
//
// **Why hysteresis and not a single threshold.** A tracked fingertip held against a virtual
// button jitters by about a millimetre. A single threshold at 8 mm turns that jitter into a
// press/release/press/release at the frame rate the instant a finger hovers near it — a flipper
// machine-gunning at 72 Hz, which is both unplayable and, because each edge is a real flipper
// press, audible as a buzz. Pressing at 8 mm and releasing at 4 mm means a 4 mm dead band, which
// is four times the jitter.
//
// **Why the press edge is reported and not just the state.** A press is a *moment*: the click,
// the solenoid thunk, the visible depression and the haptic pulse all fire on it and all have to
// happen within the same simulation step (06 §2.1). A caller polling `pressed` would fire them
// on every frame the button was held.
//
// **Controllers do not use the travel at all.** 06 §2.1 is explicit that the button is the
// *context* and the trigger is the *action*: a controller near the button with its trigger
// pulled presses it, with no reaching precision required. That is `pressWithTrigger` below, and
// the reason it is the same gate rather than a second one is that the release, the edge and the
// multimodal confirmation are identical — only the test for "down" differs.

import type { PressableControl } from './cabinetControls'
import { HOVER_REACH, inwardTravel, offAxis, PRESS_DEPTH, PRESS_TRAVEL, RELEASE_TRAVEL } from './cabinetControls'

/** What happened to one button this frame. */
export interface PressState {
  /** Is it down? */
  readonly down: boolean
  /** Did it go down *this frame*? The moment the click, thunk, depression and haptic fire on. */
  readonly pressedEdge: boolean
  /** Did it come up this frame? */
  readonly releasedEdge: boolean
  /** How far in it is, 0 … 1, for drawing the button depressed. */
  readonly depression: number
  /**
   * Is a fingertip in the button's zone — within its radius, no further than `HOVER_REACH` in
   * front of the cap — or the button down? The rail lamp's green (14 Sep 2026). A controller in
   * its widened capsule and a bound control that is held count too.
   */
  readonly over: boolean
}

const IDLE: PressState = { down: false, pressedEdge: false, releasedEdge: false, depression: 0, over: false }

export class PressGate {
  private readonly control: PressableControl
  private state = false

  constructor(control: PressableControl) {
    this.control = control
  }

  get pressed(): boolean {
    return this.state
  }

  /**
   * A tracked fingertip at `(x, y, z)`, already velocity-led by the caller.
   *
   * Led by the caller rather than here because the lead is a property of the *input source* —
   * 40 ms for a hand, 4 ms for a controller (06 §8) — and a gate that led its own input would
   * need to know which kind of hand was pressing it.
   */
  advanceFingertip(x: number, y: number, z: number): PressState {
    // **Off the axis, not away from the centre** (M8-28). A sphere here made the button shrink as
    // it was pressed and let a committed press push straight out of the far side — see `offAxis`,
    // which carries the device measurements.
    const travel = inwardTravel(this.control, x, y, z)
    const within = offAxis(this.control, x, y, z) <= this.control.radius && travel <= PRESS_DEPTH
    if (!within) return this.settle(false)
    // Hysteresis: cross 8 mm to go down, fall back below 4 mm to come up.
    const down = this.state ? travel > RELEASE_TRAVEL : travel > PRESS_TRAVEL
    return this.settle(down, travel, travel >= -HOVER_REACH)
  }

  /**
   * A controller near the button, with the trigger either pulled or not.
   *
   * `radius` is widened for controllers because the hand holding one is 60–80 mm behind the
   * tracked origin and nobody aims a controller at a button they are about to trigger. The
   * button is still the context — a trigger pulled with the controller at the plunger does not
   * flip — but the context is deliberately loose.
   */
  advanceController(x: number, y: number, z: number, triggerHeld: boolean, radiusScale = 2): PressState {
    // The same capsule, widened — around the *axis* here too, or a controller held deep beside
    // the cabinet stops flipping for the same reason a finger did.
    const travel = inwardTravel(this.control, x, y, z)
    const within =
      offAxis(this.control, x, y, z) <= this.control.radius * radiusScale &&
      travel <= PRESS_DEPTH * radiusScale
    return this.settle(within && triggerHeld, 0, within && travel >= -HOVER_REACH * radiusScale)
  }

  /**
   * Comfort mode, and the fallback whenever a control is bound rather than reached for.
   *
   * No positional requirement at all (06 §2.1, XR-007). The same gate so that a player who
   * switches to seated mid-ball gets the same edges, the same sounds and the same haptics.
   */
  advanceBound(held: boolean): PressState {
    return this.settle(held)
  }

  /**
   * Let go of the button because the input it was being pressed with has gone away.
   *
   * Returns the release edge if it was down, so a flipper does not stay up when a hand stops
   * being tracked mid-cradle — which is the difference between losing tracking and losing the
   * ball.
   */
  release(): PressState {
    return this.settle(false)
  }

  private settle(down: boolean, travel = 0, over = down): PressState {
    const was = this.state
    this.state = down
    if (!down && !was && !over) return IDLE
    const depression = down ? clamp01(travel / PRESS_TRAVEL, down) : 0
    return { down, pressedEdge: down && !was, releasedEdge: !down && was, depression, over: over || down }
  }
}

/**
 * Depression for drawing, 0 … 1.
 *
 * Reports 1 for a button that is down with no travel measured, which is every controller and
 * every bound press: a button that is down looks down. Reporting 0 there would draw a flipper
 * button that never moves for a controller player, which is the one group most likely to be
 * looking directly at it.
 */
function clamp01(v: number, downWithoutTravel: boolean): number {
  if (!Number.isFinite(v) || v <= 0) return downWithoutTravel ? 1 : 0
  return v >= 1 ? 1 : v
}
